import Script from 'next/script';
import Head from 'next/head';
import { Box } from '@mui/material';
import { SliceZone, PrismicRichText } from '@prismicio/react';
import * as prismicH from '@prismicio/helpers';

import Footer from '@components/Footer';
import ExitIntent from '@components/ExitIntent';
import { createClient, linkResolver } from '../prismicio';
import { components } from '../slices';
import AnnouncementBanner from '@components/AnnouncementBanner';

const Page = ({ page, navigation, settings, pages }: any) => {
  const { data, first_publication_date, last_publication_date, type } =
    page || {};
  const herSection =
    data?.slices?.find((slice: any) => slice?.slice_type === 'hero_section') ||
    {};
  const { by_whom, dish_image } = herSection?.primary || {};

  const schema = {
    '@context': 'https://schema.org',
    headline: data.seo_title,
    author: {
      '@type': 'Person',
      name: by_whom,
      // The full URL must be provided, including the website's domain.
      //url: new URL(prismicH.asLink(article.data.author), 'https://example.com'),
    },
    image: prismicH.asImageSrc(dish_image),
    datePublished: first_publication_date,
    dateModified: last_publication_date,
  };

  return (
    <>
      <Head>
        <title>{page.data.seo_title}</title>
        <meta name="description" content={page.data.description} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>
      <AnnouncementBanner bgcolor="cinnamon.main" />
      <ExitIntent isAutoShowOnFiveSec />
      <PrismicRichText field={page.data.content} />
      <Box sx={{ position: 'relative' }}>
        <SliceZone
          slices={page.data.slices}
          components={components}
          context={{ pages, pageType: type, data }}
        />
      </Box>
      <Footer />
      <Script src="https://static.cdn.prismic.io/prismic.js?new=true&repo=thecuminclub" />
    </>
  );
};

export default Page;

const CONTENT_TYPES = [
  'page',
  'story',
  'fusion_receipe',
  'announcement',
] as const;

export async function getStaticProps({ params, previewData }: any) {
  const client = createClient({ previewData });

  try {
    // Find the page by trying getByUID for each content type instead of fetching everything
    let page = null;
    for (const type of CONTENT_TYPES) {
      try {
        page = await client.getByUID(type, params.uid);
        if (page) break;
      } catch {
        // Not found in this type, try next
      }
    }

    if (!page) {
      return { notFound: true };
    }

    // Only fetch related pages of the same type
    const sameTypePages = await client.getAllByType(page.type);
    const relatedPages = sameTypePages.filter((p: any) => p.uid !== params.uid);

    return {
      props: {
        page,
        pages: relatedPages,
      },
      revalidate: 60,
    };
  } catch {
    return { notFound: true };
  }
}

export async function getStaticPaths() {
  const client = createClient();
  const allPages = (
    await Promise.all(CONTENT_TYPES.map((type) => client.getAllByType(type)))
  ).flat();

  return {
    paths: allPages.map((page) => prismicH.asLink(page, linkResolver)),
    fallback: 'blocking',
  };
}
